import React, { useMemo, useState } from "react";
import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { IconProp } from "@fortawesome/fontawesome-svg-core";
import {
  faArrowLeft,
  faFolderOpen,
  faSlidersH,
  faTags,
  faTriangleExclamation,
  faUser,
} from "@fortawesome/free-solid-svg-icons";
import AppBar from "../components/app-bar";
import CustomInput from "../components/customs/custom-input";
import CustomRadioButton from "../components/customs/custom-radio-button";
import CustomSelect from "../components/customs/custom-select";
import CustomButton from "../components/customs/custom-button";
import Loader from "../components/loader";
import RenewalDocuments from "../components/renewal-documents";
import { ParseErrors } from "../utils/ParseErrors";
import axiosInstance from "../utils/axiosInstance";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import {
  BRAND_OPTIONS,
  POLICY_SOURCE_OPTIONS,
  COVER_TYPE_OPTIONS,
  LAPSE_REASONS,
  QUOTE_SLOTS,
  QUOTE_SLOT_LABEL,
  QUOTE_LINE_FIELDS,
  timelineHint,
  hasQuoteValue,
  blankQuote,
  orderQuotes,
  withTotal,
  toNum,
  numOrNull,
  money,
  r2,
  initialForm,
  type CreateChaseForm,
  type Quote,
  type QuoteSlot,
} from "../utils/create-chase.constants";

const CreateChase: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  // Bytes of the create request sent so far — the document bars read this.
  const [loaded, setLoaded] = useState(0);

  // One state object drives the whole form; `set` shallow-merges a patch.
  const [value, setValue] = useState<CreateChaseForm>(initialForm);
  const set = (patch: Partial<CreateChaseForm>) =>
    setValue((s) => ({ ...s, ...patch }));

  const { quotes, paymentType, auto, documents } = value;

  // ── Quotes: state holds only the columns that carry data ────────────────────
  const quoteAt = (slot: QuoteSlot) =>
    quotes.find((q) => q.slot === slot) ?? blankQuote(slot);
  const activeSlot = quotes.find((q) => q.isActive)?.slot ?? "MAIN";

  const putQuote = (next: Quote) =>
    set({
      quotes: orderQuotes([
        ...quotes.filter((q) => q.slot !== next.slot),
        next,
      ]),
    });
  const setQuote = (slot: QuoteSlot, patch: Partial<Quote>) => {
    const next = withTotal({
      ...quoteAt(slot),
      ...patch,
      isActive: slot === activeSlot,
    });
    // Clearing the last value in a column drops it from the payload again.
    if (!hasQuoteValue(next))
      return set({ quotes: quotes.filter((q) => q.slot !== slot) });
    putQuote(next);
  };
  const selectQuote = (slot: QuoteSlot) => {
    const marked = quotes.map((q) => ({ ...q, isActive: q.slot === slot }));
    set({
      quotes: marked.some((q) => q.slot === slot)
        ? marked
        : orderQuotes([...marked, { ...blankQuote(slot), isActive: true }]),
    });
  };

  const monthlyPlan = useMemo(() => {
    const premium = quoteAt(activeSlot).premium;
    const rate = value.interestRate;
    const count = Math.max(0, Math.round(value.instalments));
    const totalPayable = r2(premium * (1 + rate / 100));
    const monthly = count > 0 ? r2(totalPayable / count) : 0;
    return { premium, rate, count, totalPayable, monthly };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quotes, activeSlot, value.interestRate, value.instalments]);

  const err = (msg: string) => {
    toast.error(msg);
    return false;
  };
  const validate = (): boolean => {
    if (!value.forename.trim()) return err("Please enter Fore Name");
    if (!value.surname.trim()) return err("Please enter Surname");
    if (!value.email.trim()) return err("Please enter Email");
    if (!value.telephone.trim()) return err("Please enter Phone");
    if (!value.policyReferenceNumber.trim())
      return err("Please enter Policy Reference Number");
    if (!value.versionReferenceNumber.trim())
      return err("Please enter Version Reference Number");
    if (!value.renewalDate) return err("Please select a Renewal Date");
    if (!value.brandCode) return err("Please select a Brand");
    if (!value.policySource) return err("Please select a Policy Source");
    if (quoteAt("MAIN").insurerPremium === null)
      return err("Main Invite requires an Insurer Premium");
    // Every column that carries pricing has to say who quoted it.
    const noInsurer = quotes
      .filter(hasQuoteValue)
      .find((q) => !q.insurer.trim());
    if (noInsurer)
      return err(`${QUOTE_SLOT_LABEL[noInsurer.slot]} requires an Insurer`);
    if (paymentType === "instalments") {
      if (monthlyPlan.count <= 0)
        return err(
          "Instalment policies need a number of instalments (e.g. 10).",
        );
      if (monthlyPlan.premium <= 0)
        return err(
          "Add the selected quote's pricing before using instalments.",
        );
    }
    return true;
  };

  const createChase = async () => {
    if (!validate()) return;
    setLoading(true);
    setLoaded(0);

    // The two nested arrays carry a UI-only key / total; everything else ships
    // as `...value`. Each document carries its base64 `file` — /chase puts it on
    // S3 and stores the key against the chase.
    const quotePayload = quotes
      .filter(hasQuoteValue)
      .map((q) => ({ ...q, insurer: q.insurer.trim() }));
    const documentPayload = documents.map(({ key, ...d }) => d);

    try {
      await axiosInstance.post(
        "/chase",
        {
          ...value,
          renewalDate: new Date(value.renewalDate).toISOString(),
          // Instalment-only figures; a cash policy stores none of them.
          instalments: paymentType === "instalments" ? monthlyPlan.count : null,
          interestRate: paymentType === "instalments" ? monthlyPlan.rate : null,
          monthlyAmount:
            paymentType === "instalments" ? monthlyPlan.monthly : null,
          totalPayable:
            paymentType === "instalments" ? monthlyPlan.totalPayable : null,
          // Auto-renewing policies aren't lapsing, so they carry no reason.
          lapseReason: auto ? null : value.lapseReason || null,
          quotes: quotePayload,
          documents: documentPayload,
        },
        { onUploadProgress: (event) => setLoaded(event.loaded) },
      );
      toast.success("Chase created successfully");
      navigate("/chaselist");
    } catch (error) {
      toast.error(ParseErrors(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="sticky top-0 w-full border-b border-edge z-30">
        <AppBar title="Create Chase" profileInfo />
      </div>

      <div className="max-w-[1080px] mx-auto px-4 sm:px-5 py-8">
        {/* Page header */}
        <div className="mb-7 flex items-center gap-4 animate-fade-in">
          <button
            type="button"
            onClick={() => navigate("/chaselist")}
            aria-label="Back to chase list"
            className="h-10 w-10 shrink-0 flex items-center justify-center rounded-xl border border-edge bg-surface text-light-gray hover:text-text-primary hover:border-indigo-400/40 hover:shadow-glow-sm transition-all duration-200"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>
          <div>
            <h1 className="text-[26px] sm:text-[30px] font-dmSans font-bold bg-gradient-heading bg-clip-text text-transparent leading-tight">
              Create Chase
            </h1>
            <p className="text-light-gray text-sm">
              Set up a new SME renewal — customer details, pricing and
              documents.
            </p>
          </div>
        </div>

        {/* Customer & Policy */}
        <Card>
          <SectionHeader
            icon={faUser}
            title="Customer & Policy"
            desc="Who the policy holder is and the core policy references."
          />
          <Grid>
            <CustomInput
              label="Fore Name *"
              value={value.forename}
              onChange={(e) => set({ forename: e.target.value })}
              autoComplete="off"
            />
            <CustomInput
              label="Surname *"
              value={value.surname}
              onChange={(e) => set({ surname: e.target.value })}
              autoComplete="off"
            />
            <CustomInput
              label="Company Name"
              value={value.companyName}
              onChange={(e) => set({ companyName: e.target.value })}
              autoComplete="off"
            />
            <CustomInput
              label="Email *"
              value={value.email}
              onChange={(e) => set({ email: e.target.value })}
              autoComplete="off"
            />
            <CustomInput
              label="Phone *"
              value={value.telephone}
              onChange={(e) => set({ telephone: e.target.value })}
              autoComplete="off"
            />
            <CustomInput
              label="Date of Birth"
              type="date"
              value={value.dateOfBirth}
              onChange={(e) => set({ dateOfBirth: e.target.value })}
            />
            <CustomInput
              label="Post Code"
              value={value.postcode}
              onChange={(e) => set({ postcode: e.target.value })}
              className="uppercase"
              autoComplete="off"
            />
            <CustomInput
              label="Address 1"
              value={value.address}
              onChange={(e) => set({ address: e.target.value })}
              autoComplete="off"
            />
            <CustomInput
              label="Policy Reference Number *"
              value={value.policyReferenceNumber}
              onChange={(e) => set({ policyReferenceNumber: e.target.value })}
              autoComplete="off"
            />
            <CustomInput
              label="Version Reference Number *"
              value={value.versionReferenceNumber}
              onChange={(e) => set({ versionReferenceNumber: e.target.value })}
              autoComplete="off"
            />
            <CustomSelect
              comboboxType="select"
              label="Brand *"
              labelProperty="label"
              valueProperty="value"
              options={BRAND_OPTIONS}
              value={BRAND_OPTIONS.find((o) => o.value === value.brandCode)}
              onSingleSelectChange={(o) =>
                set({ brandCode: (o as any)?.value ?? BRAND_OPTIONS[0].value })
              }
            />
            <CustomSelect
              comboboxType="select"
              label="Policy Source *"
              labelProperty="label"
              valueProperty="value"
              options={POLICY_SOURCE_OPTIONS}
              value={POLICY_SOURCE_OPTIONS.find(
                (o) => o.value === value.policySource,
              )}
              onSingleSelectChange={(o) =>
                set({
                  policySource:
                    (o as any)?.value ?? POLICY_SOURCE_OPTIONS[0].value,
                })
              }
            />
            <CustomSelect
              comboboxType="select"
              label="Cover Type"
              labelProperty="label"
              valueProperty="value"
              options={COVER_TYPE_OPTIONS}
              value={COVER_TYPE_OPTIONS.find(
                (o) => o.value === value.coverType,
              )}
              onSingleSelectChange={(o) =>
                set({
                  coverType: (o as any)?.value ?? COVER_TYPE_OPTIONS[0].value,
                })
              }
            />
            <CustomInput
              label="Policy Number"
              value={value.policyNumber}
              onChange={(e) => set({ policyNumber: e.target.value })}
              autoComplete="off"
            />
            <CustomInput
              label="Insurer Name"
              value={value.insurer}
              onChange={(e) => set({ insurer: e.target.value })}
              autoComplete="off"
            />
            <CustomInput
              label="Renewal Date *"
              type="date"
              value={value.renewalDate}
              onChange={(e) => set({ renewalDate: e.target.value })}
              helperText={timelineHint(value.renewalDate)}
            />
          </Grid>
        </Card>

        {/* Policy Options */}
        <Card>
          <SectionHeader
            icon={faSlidersH}
            title="Policy Options"
            desc="Risk, payment and renewal behaviour for this chase."
          />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <ChoiceGroup label="Risk Type">
              <CustomRadioButton
                id="rk-std"
                name="policyType"
                label="Standard"
                value="Standard"
                checked={value.policyType === "Standard"}
                onChange={() => set({ policyType: "Standard" })}
              />
              <CustomRadioButton
                id="rk-non"
                name="policyType"
                label="Non-Standard"
                value="Non-Standard"
                checked={value.policyType === "Non-Standard"}
                onChange={() => set({ policyType: "Non-Standard" })}
              />
            </ChoiceGroup>
            <ChoiceGroup label="Payment Type">
              <CustomRadioButton
                id="pt-cash"
                name="paymentType"
                label="Cash"
                value="cash"
                checked={paymentType === "cash"}
                onChange={() => set({ paymentType: "cash" })}
              />
              <CustomRadioButton
                id="pt-inst"
                name="paymentType"
                label="Instalments"
                value="instalments"
                checked={paymentType === "instalments"}
                onChange={() => set({ paymentType: "instalments" })}
              />
            </ChoiceGroup>
            <ChoiceGroup label="Auto Renew">
              <CustomRadioButton
                id="ar-true"
                name="auto"
                label="True"
                value="true"
                checked={auto === true}
                onChange={() => set({ auto: true })}
              />
              <CustomRadioButton
                id="ar-false"
                name="auto"
                label="False"
                value="false"
                checked={auto === false}
                onChange={() => set({ auto: false })}
              />
            </ChoiceGroup>
          </div>

          {(!auto || paymentType === "instalments") && (
            <div className="mt-6 pt-6 border-t border-dashed border-edge grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-5">
              {!auto && (
                <div className="sm:col-span-2">
                  <CustomSelect
                    comboboxType="select"
                    label="Lapse Reason (why isn't this policy auto-renewing?)"
                    placeholder="Select a reason…"
                    labelProperty="label"
                    valueProperty="value"
                    options={LAPSE_REASONS}
                    value={LAPSE_REASONS.find(
                      (o) => o.value === value.lapseReason,
                    )}
                    onSingleSelectChange={(o) =>
                      set({ lapseReason: (o as any)?.value ?? "" })
                    }
                  />
                </div>
              )}
              {paymentType === "instalments" && (
                <>
                  <CustomInput
                    label="Instalments"
                    type="number"
                    value={value.instalments}
                    onChange={(e) =>
                      set({ instalments: toNum(e.target.value) })
                    }
                  />
                  <CustomInput
                    label="Interest Rate (%)"
                    type="number"
                    value={value.interestRate}
                    onChange={(e) =>
                      set({ interestRate: toNum(e.target.value) })
                    }
                  />
                </>
              )}
            </div>
          )}
        </Card>

        {/* Invite pricing */}
        <Card>
          <SectionHeader
            icon={faTags}
            title="Invite Pricing"
            desc="Main invite plus up to four alternatives. Pick which quote is sent — its total becomes the headline premium."
          />
          <div className="overflow-x-auto -mx-1 px-1 custom-scrollbar">
            <table className="w-full min-w-[720px] border-collapse">
              <thead>
                <tr>
                  <th className="w-32" />
                  {QUOTE_SLOTS.map((slot) => (
                    <th
                      key={slot}
                      className="px-1.5 pb-3 text-left align-bottom"
                    >
                      <label
                        className={clsx(
                          "flex items-center gap-2 cursor-pointer text-[13px] font-semibold rounded-lg border px-2.5 py-2 transition-colors duration-200",
                          activeSlot === slot
                            ? "border-indigo-400/50 bg-active-background text-text-primary"
                            : "border-edge text-light-gray hover:text-text-primary hover:border-indigo-400/30",
                        )}
                      >
                        <input
                          type="radio"
                          name="activeQuote"
                          checked={activeSlot === slot}
                          onChange={() => selectQuote(slot)}
                          className="accent-indigo-500"
                        />
                        {QUOTE_SLOT_LABEL[slot]}
                      </label>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="text-light-gray text-sm py-2 pr-3">
                    Insurer *
                  </td>
                  {QUOTE_SLOTS.map((slot) => (
                    <td key={slot} className="px-1.5 py-1">
                      <input
                        className="border border-input-border rounded-lg bg-input-bg text-text-input text-sm px-3 h-[38px] w-full outline-none transition-colors duration-200 focus:border-indigo-400/50 focus:ring-1 focus:ring-indigo-500/30"
                        placeholder="e.g. Aviva"
                        value={quoteAt(slot).insurer}
                        onChange={(e) =>
                          setQuote(slot, { insurer: e.target.value })
                        }
                      />
                    </td>
                  ))}
                </tr>
                {QUOTE_LINE_FIELDS.map((f) => (
                  <tr key={f.key}>
                    <td className="text-light-gray text-sm py-2 pr-3">
                      {f.label}
                    </td>
                    {QUOTE_SLOTS.map((slot) => (
                      <td key={slot} className="px-1.5 py-1">
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-light-gray text-sm">
                            £
                          </span>
                          <input
                            className="border border-input-border rounded-lg bg-input-bg text-text-input text-sm pl-6 pr-3 h-[38px] w-full outline-none transition-colors duration-200 focus:border-indigo-400/50 focus:ring-1 focus:ring-indigo-500/30"
                            type="number"
                            value={quoteAt(slot)[f.key] ?? ""}
                            onChange={(e) =>
                              setQuote(slot, {
                                [f.key]: numOrNull(e.target.value),
                              })
                            }
                          />
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
                <tr>
                  <td className="text-text-primary text-sm font-semibold py-3 pr-3 border-t border-edge">
                    Total
                  </td>
                  {QUOTE_SLOTS.map((slot) => (
                    <td
                      key={slot}
                      className={clsx(
                        "px-2.5 py-3 font-bold text-sm border-t border-edge",
                        activeSlot === slot ? "text-blue" : "text-text-primary",
                      )}
                    >
                      {money(quoteAt(slot).premium)}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>

          {paymentType === "instalments" && (
            <div className="mt-5 rounded-xl border border-edge bg-active-background/60 px-4 py-3 text-sm text-text-secondary">
              <span className="font-semibold text-text-primary">
                Payment overview:
              </span>{" "}
              {money(monthlyPlan.monthly)} / month over {monthlyPlan.count}{" "}
              instalments — total payable{" "}
              <span className="font-semibold text-text-primary">
                {money(monthlyPlan.totalPayable)}
              </span>
              .
            </div>
          )}
        </Card>

        {/* Renewal documents */}
        <Card>
          <SectionHeader
            icon={faFolderOpen}
            title="Renewal Documents"
            desc="Attach the renewal pack. TOBA can carry a fee."
          />
          <div className="mb-4 flex items-start gap-2.5 rounded-xl border border-amber-500/40 bg-amber-500/10 px-3.5 py-3 text-[13px] text-amber-600">
            <FontAwesomeIcon
              icon={faTriangleExclamation}
              className="mt-0.5 shrink-0"
            />
            <span>
              These documents will be uploaded under quote ref{" "}
              <b>{value.versionReferenceNumber.trim() || "—"}</b>. When the
              policy is renewed, the new documents will be uploaded under the
              live ref.
            </span>
          </div>
          <RenewalDocuments
            documents={documents}
            onChange={(documents) => set({ documents })}
            loading={loading}
            loaded={loaded}
          />
        </Card>

        {/* Footer actions */}
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 mt-2 mb-12">
          <button
            type="button"
            onClick={() => navigate("/chaselist")}
            disabled={loading}
            className="h-[50px] px-6 rounded-lg border border-edge bg-surface text-text-primary font-medium hover:border-indigo-400/40 hover:bg-active-background transition-colors duration-200 disabled:opacity-50"
          >
            Cancel
          </button>
          <CustomButton
            className="uppercase sm:min-w-[240px]"
            variant="primary"
            onClick={createChase}
            disabled={loading}
          >
            {loading ? <Loader /> : "Create Chase"}
          </CustomButton>
        </div>
      </div>
    </div>
  );
};

const Card: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  // relative + focus-within:z lifts the whole card (animate-fade-in's persisted
  // transform makes it a stacking context) so open dropdowns clear later cards
  <section className="relative z-0 focus-within:z-20 bg-surface rounded-2xl border border-edge shadow-card p-5 sm:p-7 mb-6 animate-fade-in">
    {children}
  </section>
);

const SectionHeader: React.FC<{
  icon: IconProp;
  title: string;
  desc?: string;
  action?: React.ReactNode;
}> = ({ icon, title, desc, action }) => (
  <div className="flex items-start justify-between gap-4 mb-6">
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 shrink-0 rounded-xl flex items-center justify-center text-white bg-gradient-to-br from-accent to-accent-bright shadow-sm shadow-indigo-500/30">
        <FontAwesomeIcon icon={icon} />
      </div>
      <div>
        <h2 className="text-heading-color font-bold text-[17px] leading-tight">
          {title}
        </h2>
        {desc && (
          <p className="text-light-gray text-[13px] mt-0.5 max-w-[46ch]">
            {desc}
          </p>
        )}
      </div>
    </div>
    {action}
  </div>
);

const Grid: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-5">
    {children}
  </div>
);

const ChoiceGroup: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div className="rounded-xl border border-edge bg-surface-muted/60 p-4">
    <label className="text-label-muted font-medium block text-sm mb-3">
      {label}
    </label>
    <div className="flex flex-wrap gap-4">{children}</div>
  </div>
);

export default CreateChase;
